import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import AdminLayout from '../components/layout/AdminLayout';
import { Package, Users, Mail, CheckCircle, Clock, TrendingUp } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/api/admin/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setStats(response.data);
      } catch (error) {
        console.error('Error fetching stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [token]);

  const statCards = [
    {
      title: 'Total Products',
      value: stats?.total_products || 0,
      icon: Package,
      color: 'bg-blue-50 text-blue-600',
      link: '/admin/products'
    },
    {
      title: 'Approved Products',
      value: stats?.approved_products || 0,
      icon: CheckCircle,
      color: 'bg-green-50 text-green-600',
      link: '/admin/products'
    },
    {
      title: 'Pending Approval',
      value: stats?.pending_products || 0,
      icon: Clock,
      color: 'bg-yellow-50 text-yellow-600',
      link: '/admin/products'
    },
    {
      title: 'Newsletter Subscribers',
      value: stats?.total_subscribers || 0,
      icon: Mail,
      color: 'bg-purple-50 text-purple-600',
      link: '/admin/newsletter'
    },
  ];

  return (
    <AdminLayout title="Dashboard">
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="spinner"></div>
        </div>
      ) : (
        <div className="space-y-8" data-testid="admin-dashboard">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {statCards.map((card) => (
              <Link
                key={card.title}
                to={card.link}
                className="bg-white p-6 border hover:shadow-md transition-shadow"
                data-testid={`stat-card-${card.title.toLowerCase().replace(' ', '-')}`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-neutral-500 mb-1">{card.title}</p>
                    <p className="text-3xl font-semibold">{card.value}</p>
                  </div>
                  <div className={`p-3 ${card.color}`}>
                    <card.icon className="w-6 h-6" />
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Quick Actions */}
          <div className="bg-white p-6 border">
            <h2 className="text-lg font-heading font-semibold mb-6">Quick Actions</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Link
                to="/admin/products/add"
                className="flex items-center gap-4 p-4 border hover:bg-neutral-50 transition-colors"
                data-testid="quick-action-add-product"
              >
                <div className="p-3 bg-blue-50 text-blue-600">
                  <Package className="w-6 h-6" />
                </div>
                <div>
                  <p className="font-medium">Add New Product</p>
                  <p className="text-sm text-neutral-500">Add a product with Amazon link</p>
                </div>
              </Link>
              
              <Link
                to="/admin/products"
                className="flex items-center gap-4 p-4 border hover:bg-neutral-50 transition-colors"
                data-testid="quick-action-manage-products"
              >
                <div className="p-3 bg-green-50 text-green-600">
                  <CheckCircle className="w-6 h-6" />
                </div>
                <div>
                  <p className="font-medium">Manage Products</p>
                  <p className="text-sm text-neutral-500">Approve or deny products</p>
                </div>
              </Link>
              
              <Link
                to="/admin/newsletter"
                className="flex items-center gap-4 p-4 border hover:bg-neutral-50 transition-colors"
                data-testid="quick-action-newsletter"
              >
                <div className="p-3 bg-purple-50 text-purple-600">
                  <Mail className="w-6 h-6" />
                </div>
                <div>
                  <p className="font-medium">View Subscribers</p>
                  <p className="text-sm text-neutral-500">Newsletter subscriber list</p>
                </div>
              </Link>
            </div>
          </div>

          {/* Info Card */}
          <div className="bg-neutral-50 border-l-4 border-champagne p-6">
            <h3 className="font-medium mb-2 flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Affiliate Program
            </h3>
            <p className="text-sm text-neutral-600">
              All products are linked with your Amazon affiliate ID: <code className="bg-white px-2 py-0.5 border">k9corner02-20</code>. 
              You earn commission on every purchase made through your links.
            </p>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminDashboard;
